import {setHours, setMilliseconds, setMinutes, setSeconds} from "date-fns";
import {TFunction} from "i18next";

interface OptionsDictionary {
    [key: string]: { start: string, end: string };
}

interface OptionsDict {
    [key: string]: number;
}

interface Timezone {
    en: string;
    fr: string;
}

export const isPreferenceSubmissionPeriod = (submitPreferenceDeadline: Date | undefined): boolean => {
    return submitPreferenceDeadline ? new Date() <= submitPreferenceDeadline : false;
}

/** Returns true if today is on or before the mentor selection deadline (used for admin availability edits). */
export const isBeforeMentorSelectionDeadline = (mentorSelectionDeadline: Date | undefined): boolean => {
    return mentorSelectionDeadline ? new Date() <= mentorSelectionDeadline : true;
}

export type MenteePeriod = 'unknown' | 'preference-submission' | 'mentor-selection' | 'after-selection';

export const getMenteePeriod = (
    submitPreferenceDeadline: Date | undefined,
    selectMentorDeadline: Date | undefined
): MenteePeriod => {
    const today = new Date();
    if (!submitPreferenceDeadline || !selectMentorDeadline) return 'unknown';
    if (today <= submitPreferenceDeadline) return 'preference-submission';
    if (today <= selectMentorDeadline) return 'mentor-selection';
    return 'after-selection';
};

export const isMentorSelectionPeriod = (submitPreferenceDeadline: Date | undefined, selectMentorDeadline: Date | undefined): boolean => {
    return getMenteePeriod(submitPreferenceDeadline, selectMentorDeadline) === 'mentor-selection';
}

export const timezoneResolver: Record<string, Timezone> = {
    PDT: {en: "Pacific Daylight Time (UTC -7h)", fr: "Heure avancée du Pacifique (UTC -7h)"},
    MDT: {en: "Mountain Daylight Time (UTC -6h)", fr: "Heure avancée des Rocheuses (UTC -6h)"},
    CDT: {en: "Central Daylight Time (UTC -5h)", fr: "Heure avancée du Centre (UTC -5h)"},
    EDT: {en: "Eastern Daylight Time (UTC -4h)", fr: "Heure avancée de l'Est (UTC -4h)"},
    ADT: {en: "Atlantic Daylight Time (UTC -3h)", fr: "Heure avancée de l'Atlantique (UTC -3h)"},
    NDT: {en: "Newfoundland Daylight Time (UTC -2h)", fr: "Heure avancée de Terre-Neuve (UTC -2h)"},
}

export const getTimeAgoString = (timestamp: string | undefined, translator: TFunction<"translator", undefined>) => {
    if (!timestamp) {
        return translator('timeAgo.never');
    }

    const currentDate = new Date().getTime();
    const lastLoggedInDate = new Date(timestamp).getTime();
    const timeDifference = currentDate - lastLoggedInDate;

    const seconds = Math.floor(timeDifference / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
        return translator('timeAgo.day', { count: days });
    } else if (hours > 0) {
        return translator('timeAgo.hour', { count: hours });
    } else if (minutes > 0) {
        return translator('timeAgo.minute', { count: minutes });
    } else {
        return translator('timeAgo.justNow');
    }
};

/**
 * Converts a time to 12-hour format (e.g. "2:30 PM").
 * Accepts: "HH:mm" or "HH:mm:ss" string, or ISO/Date (time part is extracted).
 */
export const convertTo12Hour = (timeInput: string | Date): string => {
    let timeString: string;
    if (timeInput instanceof Date) {
        timeString = timeInput.toISOString().slice(11, 19); // "HH:mm:ss"
    } else if (typeof timeInput === "string" && timeInput.includes("T")) {
        // ISO string from API (e.g. "1970-01-01T05:30:00.000Z")
        timeString = timeInput.slice(11, 19);
    } else {
        timeString = String(timeInput ?? "");
    }
    if (!timeString) return "";
    const time = new Date(`2022-01-01T${timeString}`);
    if (Number.isNaN(time.getTime())) return "";
    return time.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
};

/**
 * Converts API session time "HH:mm – HH:mm" (24h) to 12h, e.g. "10:30 AM – 11:30 AM".
 */
export const formatSessionTime12h = (sessionTime: string): string => {
    const parts = sessionTime.split(/ [–-] /).map((p) => p.trim());
    if (parts.length !== 2) return sessionTime;
    const start = convertTo12Hour(parts[0]);
    const end = convertTo12Hour(parts[1]);
    return start && end ? `${start} – ${end}` : sessionTime;
};

// generate 30-min time windows from 12am to 11:30pm
export const getTimesInHalfHourIntervals = () => {
    const options: OptionsDict = {};
    let index = 0;
    for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
            const hour12 = hour % 12 || 12; // Convert hour to 12-hour format
            const period = hour < 12 ? 'AM' : 'PM'; // Determine AM or PM
            const time = `${hour12.toString()}:${minute.toString().padStart(2, '0')} ${period}`;
            options[time] = index;
            index++;
        }
    }
    options["11:59 PM"] = index;
    return options;
};

export const getTimeWindows = () => {
    const options: OptionsDictionary = {};
    const startHour = 8; // Start time at 8 AM
    const endHour = 20;  // End time at 8 PM

    for (let hour = startHour; hour < endHour; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
            const hour12 = hour % 12 || 12; // Convert hour to 12-hour format
            const period = hour < 12 ? 'AM' : 'PM'; // Determine AM or PM
            const time = `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;

            const nextHour = (hour + 1) % 24;
            const nextHour12 = nextHour % 12 || 12; // Convert hour to 12-hour format
            const nextPeriod = nextHour < 12 ? 'AM' : 'PM'; // Determine AM or PM

            const endTime = `${nextHour12}:${minute.toString().padStart(2, '0')} ${nextPeriod}`;
            options[time] = { start: time, end: endTime };
        }
    }
    return options;
};

export const setDeadline = (selectedDate: Date) => {
    // Set the time to 11:59 PM
    selectedDate = setHours(selectedDate, 23);
    selectedDate = setMinutes(selectedDate, 59);
    selectedDate = setSeconds(selectedDate, 0);
    selectedDate = setMilliseconds(selectedDate, 0);
    return selectedDate;
}

export const getDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

/** Sort key for items with sessionDate (YYYY-MM-DD) and optional sessionTime. Chronological (oldest first). */
export const sortBySessionDateAsc = <T extends { sessionDate: string; sessionTime?: string }>(a: T, b: T): number => {
    const d = a.sessionDate.localeCompare(b.sessionDate);
    if (d !== 0) return d;
    return (a.sessionTime ?? "").localeCompare(b.sessionTime ?? "");
};

/** Sort key for items with sessionDate/sessionTime. Newest first. */
export const sortBySessionDateDesc = <T extends { sessionDate: string; sessionTime?: string }>(a: T, b: T): number => {
    const d = b.sessionDate.localeCompare(a.sessionDate);
    if (d !== 0) return d;
    return (b.sessionTime ?? "").localeCompare(a.sessionTime ?? "");
};

export const getDayOfWeek = (date: Date) => {
    return date.toLocaleString('en-US', {
        weekday: 'long',
    });
}

/**
 * Normalizes a date-only value (stored as midnight UTC) so it displays as the same calendar day
 * in the user's local timezone (e.g. toDateString(), getDate(), getDayOfWeek()).
 * Revisit if: server sends timezone-aware dates, or we need to display in a specific TZ (e.g. mentor's).
 */
export const adjustTimezone = (date: Date) => {
    const d = new Date(date);
    return new Date(d.getTime() + d.getTimezoneOffset() * 60 * 1000);
}
